import moment, {Moment} from "moment";
import {AsyncTaskStatus} from "../../../model/AsyncTask";
import React, {ReactElement, useEffect, useMemo, useState} from "react";
import {ACCOUNTANT_MONTH_PATTERN, formatDate} from "../../../utils/DataFormat";
import LoaderWithCompanySelected from "../../../components/LoaderWithCompanySelected";
import {Divider, Grid, Typography} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import ButtonWithConfirmation from "../../../components/button/ButtonWithConfirmation";
import LoadingButton from "../../../components/button/LoadingButton";
import Loader from "../../../components/Loader";
import {ResultOrErrorDto} from "../../../model/ResultOrErrorDto";
import {InvoiceReviewProblem} from "../../../model/invoice/InvoiceReviewProblem";
import {Column} from "material-table";
import {companyAccountsStore} from "../../../store/CompanyAccounts";
import {uiStateStore} from "../../../store/UiState";
import {useNavigate} from 'react-router-dom';
import {DatePicker} from "../../../components/picker/DatePicker";

const useStyles = makeStyles(() =>
        ({
            table: {
                "& .MuiTableCell-root": {
                    padding: "0.1rem"
                },
                "& #problems": {}
            },
        }),
);

export interface InvoiceReviewParams<T> {
    initTaskId?: string;
    route: string;
    api: {
        startReviewGeneration: (month: Moment) => Promise<string>,
        fetchReviewStatus: (taskId: string) => Promise<AsyncTaskStatus>,
        fetchReviewResult: (taskId: string) => Promise<ResultOrErrorDto<T>>
    }
    renderResults: (result: T) => ReactElement
}

export function useInvoiceReview<T extends { month: string }>({initTaskId, route, api, renderResults}: InvoiceReviewParams<T>) {
    const navigate = useNavigate();
    const styles = useStyles();

    const [taskId, setTaskId] = useState(initTaskId)
    const [selectedDate, setSelectedDate] = useState<Moment>(() => {
        if (uiStateStore.reviewMonthSelected) {
            return moment(uiStateStore.reviewMonthSelected)
        } else {
            return moment().startOf("month").subtract(1, 'month')
        }
    });

    useEffect(() => {
        uiStateStore.reviewMonthSelected = selectedDate?.toISOString();
    }, [selectedDate])

    const [taskInProgress, setTaskInProgress] = useState(Boolean(taskId))
    const [accountsFetched, setAccountsFetched] = useState(false)
    const [result, setResult] = useState<ResultOrErrorDto<T>>()

    const terminateGeneration = () => {
        setTaskInProgress(false);
        setTaskId(undefined);
        setResult(undefined);
    }

    useEffect(() => {
        companyAccountsStore
                .refresh()
                .then(() => setAccountsFetched(true))
    }, [])

    const startNewTask = async () => {
        if (selectedDate) {
            setTaskInProgress(true);
            setResult(undefined)
            const taskId = await api.startReviewGeneration(selectedDate);
            setTaskId(taskId)

            navigate(route.replace(":id", taskId));
        }
    }

    const refreshTaskStatus = async () => {
        if (taskId) {
            try {
                const status = await api.fetchReviewStatus(taskId);
                if (status === "ERROR" || status === "FINISHED") {
                    const result = await api.fetchReviewResult(taskId);
                    setResult(result);
                    setTaskInProgress(false);
                    const month = result?.result?.month;
                    if (month) {
                        setSelectedDate(moment(month, ACCOUNTANT_MONTH_PATTERN))
                    }
                }
            } catch (e) {
                setResult({error: "This result is no longer available, please re-generate report"});
                setTaskInProgress(false);
            }
        }
    }

    const resultComponent = useMemo(() => result && result.result ? renderResults(result.result) : "No result", [result, renderResults])
    const minDate = moment().startOf('month').subtract(2, 'years').startOf('year');
    const maxDate = moment().endOf('year');

    return <>
        <LoaderWithCompanySelected loading={false}>
            <Grid container spacing={1}>
                <Grid item container xs={12} alignItems={"center"} spacing={1}>
                    <Grid item>
                        <Typography>
                            Month to review:
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Grid item container spacing={1}>
                            <Grid item style={{maxWidth: "6.5rem"}}>
                                <DatePicker
                                        views={["month"]}
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        value={selectedDate}
                                        onChange={date => setSelectedDate(date!!)}
                                        disabled={taskInProgress}
                                        inputFormat={'MMM'}
                                        mask={undefined}
                                />
                            </Grid>
                            <Grid item style={{maxWidth: "6.5rem"}}>
                                <DatePicker
                                        views={["year"]}
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        value={selectedDate}
                                        onChange={date => setSelectedDate(date!!)}
                                        disabled={taskInProgress}
                                        inputFormat={'YYYY'}
                                        mask={undefined}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <ButtonWithConfirmation onClick={taskInProgress ? terminateGeneration : startNewTask} variant={"contained"}
                                                color={taskInProgress ? "secondary" : "primary"}>
                            {taskInProgress ? "Cancel generation" : "Generate review"}
                        </ButtonWithConfirmation>
                    </Grid>

                    {taskInProgress &&
                            <Grid item>
                                <LoadingButton variant="contained" onClick={refreshTaskStatus} refreshInterval={15}>
                                    Refresh status
                                </LoadingButton>
                            </Grid>
                    }
                </Grid>

                <Grid item xs={12} className={styles.table}>
                    <Divider/>
                    {(result || taskInProgress) &&
                            <Loader loading={taskInProgress || !accountsFetched} error={result?.error}>
                                {resultComponent}
                            </Loader>
                    }
                </Grid>
            </Grid>
        </LoaderWithCompanySelected>
    </>;
}


export function reviewDateColumn<T extends { date: Date }>(): Column<T>[] {
    return [
        {title: 'Date', render: (data: T) => formatDate(data.date), field: 'date', type: 'date', defaultSort: "asc", export: false, align: "center"},
        {title: 'Date', field: 'formattedDate', export: true, hidden: true}
    ]
}

export function reviewTxDateColumn<T extends { transactionDate: Date }>(): Column<T>[] {
    return [
        {title: 'Tx date', render: (data) => formatDate(data.transactionDate), field: 'transactionDate', type: 'date', export: false, align: "center"},
        {title: 'Tx date', field: 'formattedTransactionDate', export: true, hidden: true}
    ]
}

export const exportProblemsColumn = {title: 'Problems', field: 'formattedProblems', export: true, hidden: true};

export const accountColumn = [
    {title: 'Account', render: (data: any) => <>{data.accountCode}{data.accountDto ? <><br/>{data.accountDto.name}</> : ""}</>, export: false, align: "center", sorting: false},
    {title: 'Account', field: 'formattedAccount', export: true, hidden: true}
] as Column<any>[];


export function prepareDataForReview<T extends { date: Date, transactionDate: Date, accountCode: string, problems: InvoiceReviewProblem[] }>(initial: T[]) {
    if (initial) {
        return initial
                .slice()
                .sort((a, b) => a.date.getTime() - b.date.getTime())
                .map(it => {
                    const accountDto = companyAccountsStore.accounts.find(acc => acc.accountCode === it.accountCode);
                    return ({
                        ...it,
                        formattedDate: formatDate(it.date),
                        formattedTransactionDate: formatDate(it.transactionDate),
                        formattedProblems: (it.problems ?? []).map(p => `${p.level} - ${p.category} - ${p.description}`).join("\n"),
                        accountDto: accountDto,
                        formattedAccount: accountDto ? `${accountDto.accountCode} - ${accountDto.name}` : it.accountCode,
                    });
                });
    } else {
        return [];
    }
}